import { toCrawlableInternalPath } from "./canonicalPath";

type CategoryList = {
  content?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

type CanonicalBrandList = {
  content: Array<Record<string, unknown>>;
  total: number;
};

const normalizedName = (value: unknown): string =>
  String(value || "")
    .replace(/&amp;/gi, "&")
    .trim()
    .toLowerCase();

/**
 * SEO Orchestrator defines direct children of /brands as the public canonical
 * brand pages. Manufacturer records intentionally live in a technical URL
 * namespace, so they must not be used as visible or structured-data links.
 */
export function toCanonicalBrandCategoryList(
  categoriesList: CategoryList | null | undefined
): CanonicalBrandList | null {
  const rootCategories = categoriesList?.content;
  if (!Array.isArray(rootCategories)) return null;

  const brandsParent =
    rootCategories.find(
      (category) => toCrawlableInternalPath(category?.url) === "/brands"
    ) ||
    rootCategories.find(
      (category) => normalizedName(category?.name) === "brands"
    );
  const brandCategories = Array.isArray(brandsParent?.categories)
    ? brandsParent.categories
    : [];
  const seenPaths = new Set<string>();
  const content = brandCategories
    .reduce<Array<Record<string, unknown>>>((items, category) => {
      const name = String(category?.name || "").trim();
      const path = toCrawlableInternalPath(category?.url);
      if (
        !name ||
        !path ||
        path === "/brands" ||
        /^\/manufacturer-/i.test(path) ||
        seenPaths.has(path)
      ) {
        return items;
      }

      seenPaths.add(path);
      items.push({
        ...category,
        name,
        url: path,
      });
      return items;
    }, [])
    .sort((left, right) =>
      String(left.name).localeCompare(String(right.name), "en", {
        sensitivity: "base",
      })
    );

  return content.length > 0 ? { content, total: content.length } : null;
}
